import { jwtDecode } from 'jwt-decode';
import type { AuthTokens, UserInfo } from '../../types/auth';

// Storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_INFO_KEY = 'user_info';

/**
 * Token Service
 * Manages JWT token storage and retrieval in localStorage
 */
export const tokenService = {
  /**
   * Get the access token from localStorage
   * @returns The access token or null if not found
   */
  getAccessToken(): string | null {
    try {
      return localStorage.getItem(ACCESS_TOKEN_KEY);
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  },

  /**
   * Get the refresh token from localStorage
   * @returns The refresh token or null if not found
   */
  getRefreshToken(): string | null {
    try {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error('Error getting refresh token:', error);
      return null;
    }
  },

  /**
   * Store authentication tokens in localStorage
   * @param tokens - Object containing access and refresh tokens
   */
  setTokens(tokens: AuthTokens): void {
    try {
      localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh);
    } catch (error) {
      console.error('Error setting tokens:', error);
      throw new Error('Failed to store authentication tokens');
    }
  },

  /**
   * Clear all authentication tokens from localStorage
   */
  clearTokens(): void {
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_INFO_KEY);
    } catch (error) {
      console.error('Error clearing tokens:', error);
    }
  },

  /**
   * Check if a JWT token is expired
   * @param token - The JWT token to check
   * @returns True if the token is expired, false otherwise
   */
  isTokenExpired(token: string): boolean {
    try {
      const decoded = jwtDecode<{ exp: number }>(token);
      
      if (!decoded.exp) {
        return true;
      }

      // Get current time in seconds
      const currentTime = Date.now() / 1000;
      
      // Check if token is expired (with 30 second buffer)
      return decoded.exp < currentTime + 30;
    } catch (error) {
      console.error('Error decoding token:', error);
      return true;
    }
  },

  /**
   * Get user information from localStorage
   * @returns The user info object or null if not found
   */
  getUserInfo(): UserInfo | null {
    try {
      const userInfoStr = localStorage.getItem(USER_INFO_KEY);
      if (!userInfoStr) {
        return null;
      }
      return JSON.parse(userInfoStr) as UserInfo;
    } catch (error) {
      console.error('Error getting user info:', error);
      return null;
    }
  },

  /**
   * Store user information in localStorage
   * @param userInfo - The user information to store
   */
  setUserInfo(userInfo: UserInfo): void {
    try {
      localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
    } catch (error) {
      console.error('Error setting user info:', error);
      throw new Error('Failed to store user information');
    }
  },

  /**
   * Check if user is authenticated by validating stored tokens
   * @returns True if valid tokens exist, false otherwise
   */
  isAuthenticated(): boolean {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    if (!accessToken || !refreshToken) {
      return false;
    }

    // If access token is not expired, user is authenticated
    if (!this.isTokenExpired(accessToken)) {
      return true;
    }

    // If access token is expired but refresh token is valid, user can refresh
    return !this.isTokenExpired(refreshToken);
  },
};

export default tokenService;
